import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  ChartBarIcon,
  UserGroupIcon,
  CheckBadgeIcon,
  ClockIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import image17 from '../../../src/media/images/Two_people_looking_happy_that_they_found_their_dre (1).png';
import { useAuth } from '../../auth/firebaseAuth';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';

const PropertyListingLandingPage: React.FC = () => {
  const { currentUser } = useAuth();
  const analytics = useAnalyticsWithConsent();

  const handleCTAClick = (location: string) => {
    analytics.trackInteraction('cta_click', {
      location,
      page: 'property_listing_landing',
      userId: currentUser?.uid || 'anonymous',
    });
  };

  analytics.trackPixelPageView({
    email: currentUser?.email || undefined,
    externalId: currentUser?.uid,
  });

  // SEO Helmet block
  const seoTitle = 'List Your Rental Property in Ireland | Find Tenants Fast | RentSmart.ie';
  const seoDescription =
    'List your property for free and connect with quality Irish tenants in 3 days or less. Trusted by 100+ landlords. No credit card required. Maximize rental income with RentSmart.ie.';

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta
          name="keywords"
          content="list property, Irish tenants, landlord, rental income, property listing, Ireland, Dublin, Cork, Galway, RentSmart"
        />
        <link rel="canonical" href="https://rentsmart.ie/list-your-property" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rentsmart.ie/list-your-property" />
      </Helmet>
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-24">
          <header className="text-center mb-8 sm:mb-10 lg:mb-12">
            {/* Trust Badge */}
            <div className="mb-4 sm:mb-6">
              <span className="inline-flex items-center bg-green-100 text-green-800 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold">
                <CheckBadgeIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5" />
                100% FREE - No Credit Card Required
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-indigo-900 mb-3 sm:mb-4">
              List Your Rental Property in Ireland and{' '}
              <span className="text-indigo-600">Find Tenants Fast</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto mb-6 sm:mb-8">
              Join <strong>100+ successful Irish landlords</strong> who trust RentSmart.ie to
              connect with quality tenants across Ireland and maximize rental income.
            </p>

            {/* Primary CTA - Above the fold */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center mb-6 sm:mb-8">
              {currentUser ? (
                <Link
                  to="/landlord-dashboard"
                  onClick={() => handleCTAClick('hero')}
                  className="inline-block bg-indigo-600 text-white px-8 py-4 sm:px-10 sm:py-5 rounded-full font-bold text-lg sm:text-xl shadow-2xl hover:bg-indigo-700 transition duration-300 ease-in-out transform hover:scale-105 no-underline w-full sm:w-auto"
                >
                  Go to Listing Dashboard →
                </Link>
              ) : (
                <Link
                  to="/add-listing"
                  onClick={() => handleCTAClick('hero')}
                  className="inline-block bg-indigo-600 text-white px-8 py-4 sm:px-10 sm:py-5 rounded-full font-bold text-lg sm:text-xl shadow-2xl hover:bg-indigo-700 transition duration-300 ease-in-out transform hover:scale-105 no-underline w-full sm:w-auto animate-pulse hover:animate-none"
                >
                  List Your Property FREE →
                </Link>
              )}
            </div>

            {/* Social Proof Metrics */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 lg:gap-8 text-sm sm:text-base text-gray-600">
              <div className="flex items-center">
                <ClockIcon className="h-5 w-5 text-indigo-600 mr-2" />
                <span>
                  <strong>Average 3-day</strong> tenant placement
                </span>
              </div>
              <div className="flex items-center">
                <UserGroupIcon className="h-5 w-5 text-indigo-600 mr-2" />
                <span>
                  <strong>100+</strong> active landlords
                </span>
              </div>
              <div className="flex items-center">
                <ShieldCheckIcon className="h-5 w-5 text-indigo-600 mr-2" />
                <span>
                  <strong>98%</strong> satisfaction rate
                </span>
              </div>
            </div>
          </header>

          <section className="grid md:grid-cols-2 gap-8 md:gap-12 mb-12 sm:mb-16 lg:mb-20">
            <div className="space-y-5 sm:space-y-6">
              <FeatureItem
                icon={<CurrencyDollarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-green-500" />}
                title="Maximize Your Rental Income"
                description="Our advanced pricing algorithm ensures you get the best price for your property in the Irish rental market."
              />
              <FeatureItem
                icon={<ChartBarIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />}
                title="Reach Irish Renters Nationwide"
                description="Connect with quality tenants actively searching across Dublin, Cork, Galway, and all of Ireland."
              />
              <FeatureItem
                icon={<UserGroupIcon className="h-6 w-6 sm:h-8 sm:w-8 text-purple-500" />}
                title="Quality Irish Tenant Matching"
                description="Our sophisticated matching system helps you find reliable Irish tenants that suit your preferences."
              />
            </div>
            <div className="flex items-center justify-center">
              <img src={image17} alt="Happy landlord" className="rounded-lg shadow-2xl" />
            </div>
          </section>

          {/* Mid-page CTA */}
          <section className="text-center mb-12 sm:mb-16 lg:mb-20 bg-indigo-600 py-10 sm:py-12 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-4 sm:mb-5">
              Start Listing in Under 5 Minutes
            </h2>
            <p className="text-lg sm:text-xl text-indigo-100 mb-6 sm:mb-8 max-w-2xl mx-auto">
              No complicated forms. No hidden fees. Just results.
            </p>
            {currentUser ? (
              <Link
                to="/landlord-dashboard"
                onClick={() => handleCTAClick('mid_page')}
                className="inline-block bg-white text-indigo-600 px-8 py-4 sm:px-10 sm:py-5 rounded-full font-bold text-lg sm:text-xl shadow-2xl hover:bg-indigo-50 transition duration-300 ease-in-out transform hover:scale-105 no-underline"
              >
                Go to Listing Dashboard →
              </Link>
            ) : (
              <Link
                to="/add-listing"
                onClick={() => handleCTAClick('mid_page')}
                className="inline-block bg-white text-indigo-600 px-8 py-4 sm:px-10 sm:py-5 rounded-full font-bold text-lg sm:text-xl shadow-2xl hover:bg-indigo-50 transition duration-300 ease-in-out transform hover:scale-105 no-underline"
              >
                Get Started Free - No Credit Card →
              </Link>
            )}
          </section>

          <section className="text-center mb-12 sm:mb-16 lg:mb-20 bg-gradient-to-b from-indigo-50 to-white py-10 sm:py-12 lg:py-16 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 rounded-3xl">
            <div className="mb-3 sm:mb-4">
              <span className="inline-block bg-indigo-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-semibold uppercase tracking-wide mb-3 sm:mb-4">
                Success Stories
              </span>
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-indigo-900 mb-3 sm:mb-4">
              Trusted by Thousands of Irish Landlords
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-8 sm:mb-10 lg:mb-12">
              Real experiences from Irish property owners who found their perfect tenants
            </p>
            <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
              <Testimonial
                quote="RentSmart.ie helped me find a great tenant in just 3 days! Their platform is incredibly easy to use."
                author="Sarah L., Dublin"
              />
              <Testimonial
                quote="I've been using RentSmart.ie for some time now. It's the best platform for property listings, hands down."
                author="Michael O., Cork"
              />
              <Testimonial
                quote="The support team at RentSmart.ie is amazing. They're always there to help with any questions."
                author="Emma R., Galway"
              />
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-indigo-900 mb-5 sm:mb-6">
              Ready to Get Started?
            </h2>

            {currentUser ? (
              <Link
                to="/landlord-dashboard"
                onClick={() => handleCTAClick('bottom')}
                className="inline-block bg-indigo-600 text-white px-6 py-3 sm:px-8 sm:py-4 rounded-full font-semibold text-base sm:text-lg shadow-lg hover:bg-indigo-700 transition duration-300 ease-in-out transform hover:scale-105 no-underline"
              >
                Go to Listing Dashboard
              </Link>
            ) : (
              <Link
                to="/add-listing"
                onClick={() => handleCTAClick('bottom')}
                className="inline-block bg-indigo-600 text-white px-6 py-3 sm:px-8 sm:py-4 rounded-full font-semibold text-base sm:text-lg shadow-lg hover:bg-indigo-700 transition duration-300 ease-in-out transform hover:scale-105 no-underline"
              >
                Create Your First Listing Now
              </Link>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

const FeatureItem: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <div className="flex items-start">
    <div className="flex-shrink-0 mr-3 sm:mr-4">{icon}</div>
    <div>
      <h3 className="text-base sm:text-lg md:text-xl font-semibold text-indigo-900 mb-1.5 sm:mb-2">
        {title}
      </h3>
      <p className="text-sm sm:text-base text-gray-600">{description}</p>
    </div>
  </div>
);

const Testimonial: React.FC<{ quote: string; author: string }> = ({ quote, author }) => (
  <div className="bg-gradient-to-br from-white to-indigo-50 p-5 sm:p-6 md:p-8 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-2 border-indigo-100">
    <div className="flex items-start mb-3 sm:mb-4">
      <svg
        className="w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 text-indigo-600 opacity-50"
        fill="currentColor"
        viewBox="0 0 24 24"
      >
        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
      </svg>
    </div>
    <p className="text-gray-700 text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-5 md:mb-6 font-medium">
      "{quote}"
    </p>
    <div className="flex items-center">
      <div className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-sm sm:text-base md:text-lg mr-2.5 sm:mr-3">
        {author.charAt(0)}
      </div>
      <p className="text-indigo-700 font-bold text-xs sm:text-sm md:text-base">{author}</p>
    </div>
  </div>
);

export default PropertyListingLandingPage;
